import { computed, nextTick, reactive } from 'vue'

interface UsePopoverOptions {
  /**
   * Called before the popover is torn down — on close(), and when open()/toggle() replaces a
   * still-visible popover. Use for side effects like stopping audio.
   */
  onBeforeClose?: () => void
}

/**
 * Shared popover state management with double-nextTick repositioning.
 *
 * The double-nextTick in open() is required for Popper/Vuetify overlays:
 * setting visible=false then immediately visible=true in the same tick
 * doesn't trigger a re-position. Two nextTicks let the DOM fully reconcile.
 */
export function usePopover<T extends object>(options: UsePopoverOptions = {}) {
  const state = reactive({
    data: null as T | null,
    visible: false,
  })

  function close() {
    options.onBeforeClose?.()
    state.visible = false
    state.data = null
  }

  function open(data: T) {
    // Opening over a still-visible popover replaces it — run the close side effect
    // (e.g. stopping audio) for the popover being torn down.
    if (state.visible) {
      options.onBeforeClose?.()
    }
    state.visible = false
    state.data = null
    nextTick(() => {
      nextTick(() => {
        ;(state as { data: T | null }).data = data
        state.visible = true
      })
    })
  }

  /** Open, but close if the predicate matches current data (e.g. same sentence clicked). */
  function toggle(data: T, isSame: (current: T) => boolean) {
    if (state.visible && state.data && isSame(state.data as T)) {
      close()
      return
    }
    open(data)
  }

  return {
    close,
    data: computed(() => state.data as T | null),
    open,
    toggle,
    visible: computed(() => state.visible),
  }
}
